import { ButtonTertiary, ButtonPrimary, ButtonSecondary } from "@nova/component-btn"
import { Checkbox } from "@nova/component-checkbox"
import { ClickAwayListener, ClickAwayListenerV2 } from "@nova/component-click-away-listener"
import { WorkspaceCompactTextInput } from "@nova/component-inline-text-input"
import { ScrollableArea } from "@nova/component-scrollable-area"
import { zIndex } from "@nova/constants"
import { ActionCreatorWithPayload } from "@reduxjs/toolkit"
import {
    ContextMenu,
    ContextMenuItem,
} from "features/data-management/workspace/TODO-put-in-package/ContextMenu"
import { selectActiveContextMenu } from "features/popups/contextMenu"
import { setActiveContextMenu } from "features/popups/slice"
import { RootState } from "integrations/redux"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { useState, useRef, useMemo, useEffect, FC } from "react"
import { useDispatch } from "react-redux"
import { useDebounceDispatch } from "tools/store"
import { SidePanelMode, sidePanelModes } from "./constants"
import {
    selectSearchLayersValue,
    setSearchLayersValue,
    selectSearchAssetsValue,
    setSearchAssetsValue,
    selectSearchPDFsValue,
    setSearchPDFsValue,
    selectFilterLayersValue,
    selectSortLayersValue,
    layersSortOptions,
    setFilterLayersValue,
    setSortLayersValue,
    selectSidePanelMode,
    selectSortAssetsValue,
    setSortAssetsValue,
    assetsSortOptions,
    selectFilterAssetsValue,
    setFilterAssetsValue,
    selectSortPDFsValue,
    pdfsSortOptions,
    setSortPDFsValue,
    selectFilterPDFsValue,
    setFilterPDFsValue,
    collapseSearchAndFilter,
    expandSearchAndFilter,
    selectIsSearchAndFilterCollapsed,
    setSidePanelMode,
    selectFilterLayersCreatorId,
    setFilterLayersCreatorId,
} from "./slice.new"
import { useLayerFilterOptionCounts } from "./useSidePanelLayers"
import classNames from "classnames"
import * as Icons from "@nova/component-icons"
import { selectWorkspaceMembers } from "features/entity-caching/members/UserManagementSlice"
import {
    selectAssetEntityIdsByCreatorId,
    selectEntityIdsByCreatorId,
    selectPdfIdsByCreatorId,
} from "./slice"
import { ExpandableContent } from "@nova/component-expandable-content"
import { SegmentedControl } from "../top-bar/TODO-put-in-package/SegmentedControl"
import { PortalledContextMenu } from "features/popups/PortalledContextMenu"
import { MemberExtended } from "@nova/schemas"
import { ProfilePhoto } from "features/data-management/workspace/workspace/body/settings-and-collaborators/profile/ProfilePhoto"

const searchAndFilterDataByMode: Record<
    SidePanelMode,
    {
        selectSearchValue: (s: RootState) => string
        setSearchValue: ActionCreatorWithPayload<string, string>
        FilterMenuContent: FC<{ closeMenu: () => void }>
    }
> = {
    Layers: {
        selectSearchValue: selectSearchLayersValue,
        setSearchValue: setSearchLayersValue,
        FilterMenuContent: FilterLayersMenuContent,
    },
    Assets: {
        selectSearchValue: selectSearchAssetsValue,
        setSearchValue: setSearchAssetsValue,
        FilterMenuContent: FilterAssetsMenuContent,
    },
    PDFs: {
        selectSearchValue: selectSearchPDFsValue,
        setSearchValue: setSearchPDFsValue,
        FilterMenuContent: FilterPdfMenuContent,
    },
}

export function useHasSearch() {
    const sidePanelMode = useAppSelector(selectSidePanelMode)
    const layersSearchValue = useAppSelector(selectSearchLayersValue)
    const assetsSearchValue = useAppSelector(selectSearchAssetsValue)
    const pdfSearchValue = useAppSelector(selectSearchPDFsValue)
    const hasSearch =
        (sidePanelMode === "Layers" && layersSearchValue) ||
        (sidePanelMode === "Assets" && assetsSearchValue) ||
        (sidePanelMode === "PDFs" && pdfSearchValue)
    return hasSearch
}

export function useHasFilter() {
    const sidePanelMode = useAppSelector(selectSidePanelMode)
    const layersFilterValue = useAppSelector(selectFilterLayersValue)
    const layersFilterCreatorId = useAppSelector(selectFilterLayersCreatorId)
    const layersFilterCount = layersFilterValue.length + (!!layersFilterCreatorId ? 1 : 0)
    const assetsFilterValue = useAppSelector(selectFilterAssetsValue)
    const pdfFilterValue = useAppSelector(selectFilterPDFsValue)
    const hasFilter =
        (sidePanelMode === "Layers" && !!layersFilterCount) ||
        (sidePanelMode === "Assets" && assetsFilterValue.length) ||
        (sidePanelMode === "PDFs" && pdfFilterValue.length)
    const filterCount =
        sidePanelMode === "Layers"
            ? layersFilterCount
            : sidePanelMode === "Assets"
              ? assetsFilterValue.length
              : pdfFilterValue.length
    return { hasFilter, filterCount }
}

export function useHasSort() {
    const sidePanelMode = useAppSelector(selectSidePanelMode)
    const layersSortMode = useAppSelector(selectSortLayersValue)
    const assetsSortMode = useAppSelector(selectSortAssetsValue)
    const pdfSortMode = useAppSelector(selectSortPDFsValue)
    const hasSort =
        (sidePanelMode === "Layers" && layersSortMode !== layersSortOptions[0]) ||
        (sidePanelMode === "Assets" && assetsSortMode !== assetsSortOptions[0]) ||
        (sidePanelMode === "PDFs" && pdfSortMode !== pdfsSortOptions[0])
    return hasSort
}

export function useHasSearchOrFilter() {
    const hasSearch = useHasSearch()
    const { hasFilter } = useHasFilter()
    return hasSearch || hasFilter
}

export function SidePanelSearchAndFilter() {
    const mode = useAppSelector(selectSidePanelMode)
    const initialSearchValue = useAppSelector(searchAndFilterDataByMode[mode].selectSearchValue)
    return (
        <div className="flex justify-between gap-2">
            <SidePanelSearchInput
                key={mode}
                title={`Search ${mode}`}
                initialValue={initialSearchValue}
                action={searchAndFilterDataByMode[mode].setSearchValue}
            />
            <SidePanelFilterButton />
        </div>
    )
}

function SidePanelSearchInput({
    title,
    initialValue,
    action,
}: {
    title: string
    initialValue: string
    action: ActionCreatorWithPayload<string, string>
}) {
    const { debounceDispatch } = useDebounceDispatch(500)
    const [value, setValue] = useState(initialValue)
    return (
        <WorkspaceCompactTextInput
            name={title}
            placeholder={title}
            className="w-full"
            inputClassName="!placeholder-gray-300 hover:!placeholder-gray-100 transition-colors"
            isClearable
            leftIcon={Icons.MagnifyingGlass}
            value={value}
            handleChange={(text) => {
                setValue(text)
                debounceDispatch(action(text))
            }}
        />
    )
}

function SidePanelFilterButton() {
    const mode = useAppSelector(selectSidePanelMode)
    const isSearchAndFilterCollapsed = useAppSelector(selectIsSearchAndFilterCollapsed)
    const [menuIsOpen, setMenuIsOpen] = useState(false)
    const Content = searchAndFilterDataByMode[mode].FilterMenuContent
    const { hasFilter, filterCount } = useHasFilter()
    const hasSort = useHasSort()
    useEffect(() => {
        setMenuIsOpen(false)
    }, [mode, isSearchAndFilterCollapsed])
    return (
        <>
            <ButtonSecondary
                LeftIcon={!hasFilter ? Icons.FilterList : undefined}
                title={hasFilter ? `${filterCount}` : undefined}
                onClick={() => setMenuIsOpen(true)}
                className={classNames("!px-2", {
                    "opacity-0": menuIsOpen,
                    "!min-w-[unset]": !hasFilter,
                    "!min-w-[36px]": hasFilter,
                    "!bg-blue-700 hover:!bg-blue-700 !text-white": hasFilter || hasSort,
                })}
            />
            {menuIsOpen && (
                <ClickAwayListener onClickAway={() => setMenuIsOpen(false)}>
                    <div
                        className="absolute top-[104px] left-3 right-3 bg-gray-700 border rounded-lg border-white-200 shadow-create-menu flex flex-col select-none transition-opacity pt-4"
                        style={{
                            maxHeight: "calc(calc(100vh - 130px) * .8)",
                            zIndex: zIndex.SidePanel + 2,
                        }}
                    >
                        <Content closeMenu={() => setMenuIsOpen(false)} />
                    </div>
                </ClickAwayListener>
            )}
        </>
    )
}

function FilterLayersMenuContent({ closeMenu }: { closeMenu: () => void }) {
    const dispatch = useAppDispatch()
    const sortMode = useAppSelector(selectSortLayersValue)
    const [localSortMode, setLocalSortMode] = useState(sortMode)
    const filters = useAppSelector(selectFilterLayersValue)
    const [localFilters, setLocalFilters] = useState(filters)
    const layerFilterOptionCounts = useLayerFilterOptionCounts()
    const filterCreatorId = useAppSelector(selectFilterLayersCreatorId)
    const [localFilterCreatorId, setLocalFilterCreatorId] = useState(filterCreatorId)
    const workspaceMembers = useAppSelector(selectWorkspaceMembers)
    const entityIdsByCreatorId = useAppSelector(selectEntityIdsByCreatorId)
    const validWorkspaceMembers = useMemo(() => {
        return workspaceMembers.filter((member) => entityIdsByCreatorId[member.userId]?.length)
    }, [entityIdsByCreatorId, workspaceMembers])
    return (
        <>
            {/* Sort mode */}
            <div className="flex gap-3 items-center px-4">
                <h5 className="text-gray-100 whitespace-nowrap">Sort By</h5>
                <SortDropdown
                    sortOptions={layersSortOptions}
                    sortMode={localSortMode}
                    setSortMode={setLocalSortMode}
                />
            </div>
            <div className="h-px w-full bg-gray-500 my-4" />
            {/* By Type */}
            <div className="w-full px-4 flex justify-between">
                <small className="xs text-white-400">Filter by Type</small>
            </div>
            <ScrollableArea className="mt-2 flex-1 min-h-0">
                <div className="flex flex-col gap-[2px]">
                    {Object.keys(layerFilterOptionCounts).map((filter) => {
                        const onClick = () => {
                            if (localFilters.includes(filter)) {
                                setLocalFilters((prev) => prev.filter((f) => f !== filter))
                            } else {
                                setLocalFilters((prev) => prev.concat(filter))
                            }
                        }
                        return (
                            <div
                                key={filter}
                                className="flex justify-between items-center gap-2 relative px-4 py-[6px] hover:bg-gray-800 cursor-pointer transition-colors duration-20"
                                onClick={onClick}
                            >
                                <Checkbox
                                    isChecked={localFilters.includes(filter)}
                                    onClick={onClick}
                                    size="small"
                                />
                                <small className="w-full">{filter}</small>
                                <small className="text-nova-gray">
                                    {layerFilterOptionCounts[filter]}
                                </small>
                            </div>
                        )
                    })}
                </div>
            </ScrollableArea>
            <div className="h-px w-full bg-gray-500 my-4" />
            {/* By Creator */}
            <div className="px-4 mb-4">
                <small className="xs text-white-400">Creator</small>
                <CreatorDropdown
                    selectedCreatorId={localFilterCreatorId}
                    validCreators={validWorkspaceMembers}
                    onSelect={(memberId) => setLocalFilterCreatorId(memberId)}
                />
            </div>

            {/* Buttons */}
            <div className="flex flex-col gap-3 justify-center items-center border-t border-t-gray-500 px-3 py-2">
                <div className="w-full flex gap-2">
                    <ButtonTertiary title="Cancel" className="w-full" onClick={closeMenu} />
                    <ButtonPrimary
                        title="Apply"
                        className="w-full"
                        onClick={() => {
                            dispatch(setFilterLayersValue(localFilters))
                            dispatch(setFilterLayersCreatorId(localFilterCreatorId))
                            dispatch(setSortLayersValue(localSortMode))
                            closeMenu()
                        }}
                    />
                </div>
                {(!!localFilters.length || !!localFilterCreatorId || localSortMode !== "None") && (
                    <small
                        className="text-gray-100 hover:text-white cursor-pointer"
                        onClick={() => {
                            dispatch(setFilterLayersValue([]))
                            dispatch(setFilterLayersCreatorId(undefined))
                            dispatch(setSortLayersValue("None"))
                            closeMenu()
                        }}
                    >
                        Clear All
                    </small>
                )}
            </div>
        </>
    )
}

function CreatorDropdown({
    selectedCreatorId,
    validCreators,
    onSelect,
}: {
    selectedCreatorId: string | undefined
    validCreators: MemberExtended[]
    onSelect: (memberId: string | undefined) => void
}) {
    const buttonRef = useRef<HTMLButtonElement | null>(null)
    const dispatch = useDispatch()
    const activeSidePanelContextMenu = useAppSelector(
        selectActiveContextMenu("SidePanelMemberFilter")
    )
    const selectedCreator = validCreators.find((creator) => creator.userId === selectedCreatorId)

    useEffect(() => {
        const menu = activeSidePanelContextMenu
        return () => {
            if (menu) {
                dispatch(setActiveContextMenu(undefined))
            }
        }
    })

    return (
        <>
            <button
                onMouseDown={(e) => {
                    if (activeSidePanelContextMenu) {
                        dispatch(setActiveContextMenu(undefined))
                        return
                    }
                    e.stopPropagation()
                    const boundingClientRect = buttonRef.current?.getBoundingClientRect()
                    dispatch(
                        setActiveContextMenu({
                            type: "SidePanelMemberFilter",
                            position: {
                                top:
                                    (boundingClientRect?.y || 0) +
                                    (boundingClientRect?.height || 0),
                                left: boundingClientRect?.x || 0,
                            },
                        })
                    )
                }}
                ref={buttonRef}
                className="flex gap-2 items-center w-full bg-gray-900 px-2 py-1 text-white-1000 rounded-lg mt-2"
            >
                {!!selectedCreator ? (
                    <>
                        <div className="w-5 min-w-[20px] aspect-square rounded-[5px] overflow-hidden">
                            <ProfilePhoto
                                userId={selectedCreator.userId}
                                fullName={selectedCreator.fullName}
                            />
                        </div>
                        <h5 className="truncate">{selectedCreator.fullName}</h5>
                    </>
                ) : (
                    <>
                        <Icons.Icon Icon={Icons.MagnifyingGlass} iconClassName="text-gray-300" />
                        <h5 className="truncate">Everyone</h5>
                    </>
                )}
                <div className="grow" />
                <Icons.ArrowDropDownIcon
                    dropdownOpen={!!activeSidePanelContextMenu}
                    setDropdownOpen={() => null}
                />
            </button>
            {activeSidePanelContextMenu && (
                <PortalledContextMenu
                    position={{
                        x: activeSidePanelContextMenu.position.left,
                        y: activeSidePanelContextMenu.position.top,
                    }}
                >
                    <ClickAwayListenerV2
                        onClickAway={() => dispatch(setActiveContextMenu(undefined))}
                    >
                        <div className="border border-white-200 rounded-lg overflow-hidden flex flex-col bg-black-900 shadow-md py-2">
                            {!!selectedCreatorId && (
                                <div
                                    className="hover:bg-white-200 pl-14 pr-3 h-9 border-b border-b-white-200 cursor-pointer flex items-center text-white"
                                    onClick={() => {
                                        onSelect(undefined)
                                        dispatch(setActiveContextMenu(undefined))
                                    }}
                                >
                                    Everyone
                                </div>
                            )}
                            {validCreators.map((creator) => (
                                <div
                                    key={creator.userId}
                                    className="flex gap-3 items-center hover:bg-white-200 px-3 py-1 cursor-pointer"
                                    onClick={() => {
                                        onSelect(creator.userId)
                                        dispatch(setActiveContextMenu(undefined))
                                    }}
                                >
                                    <div className="w-8 min-w-[32px] aspect-square rounded-lg overflow-hidden">
                                        <ProfilePhoto
                                            userId={creator.userId}
                                            fullName={creator.fullName}
                                        />
                                    </div>
                                    <div className="flex flex-col">
                                        <small className="text-white">
                                            {creator.fullName || ""}
                                        </small>
                                        <small className="text-gray-200">
                                            {creator.email || ""}
                                        </small>
                                    </div>
                                    {selectedCreatorId === creator.userId && (
                                        <Icons.Icon
                                            Icon={Icons.Checkmark}
                                            iconClassName="text-green-500"
                                        />
                                    )}
                                </div>
                            ))}
                        </div>
                    </ClickAwayListenerV2>
                </PortalledContextMenu>
            )}
        </>
    )
}

function FilterAssetsMenuContent({ closeMenu }: { closeMenu: () => void }) {
    const dispatch = useAppDispatch()
    const sortMode = useAppSelector(selectSortAssetsValue)
    const [localSortMode, setLocalSortMode] = useState(sortMode)
    const filterAssetsValue = useAppSelector(selectFilterAssetsValue)
    const [localFilter, setLocalFilter] = useState(filterAssetsValue)
    const workspaceMembers = useAppSelector(selectWorkspaceMembers)
    const assetEntityIdsByCreatorId = useAppSelector(selectAssetEntityIdsByCreatorId)
    const validWorkspaceMembers = useMemo(() => {
        return workspaceMembers.filter((member) => assetEntityIdsByCreatorId[member.userId]?.length)
    }, [assetEntityIdsByCreatorId, workspaceMembers])
    const validWorkspaceMemberIds = useMemo(() => {
        return validWorkspaceMembers.map((member) => member.userId)
    }, [validWorkspaceMembers])
    useEffect(() => {
        // This is needed to handle deletes. If a user is part of the filter, then you delete all the
        // assets they created, they will be stuck in the filter. This removes all users from the
        // filter if they do not have any created assets.
        const invalidSelectedMemberIds = localFilter.filter(
            (id) => !validWorkspaceMemberIds.includes(id)
        )
        if (invalidSelectedMemberIds.length) {
            const validSelectedMemberIds = localFilter.filter((id) =>
                validWorkspaceMemberIds.includes(id)
            )
            dispatch(setFilterAssetsValue(validSelectedMemberIds))
            setLocalFilter(validSelectedMemberIds)
        }
    }, [
        dispatch,
        assetEntityIdsByCreatorId,
        localFilter,
        workspaceMembers,
        validWorkspaceMembers,
        validWorkspaceMemberIds,
    ])
    return (
        <>
            {/* Sort mode */}
            <div className="flex gap-3 items-center px-4">
                <h5 className="text-gray-100 whitespace-nowrap">Sort By</h5>
                <SortDropdown
                    sortOptions={assetsSortOptions}
                    sortMode={localSortMode}
                    setSortMode={setLocalSortMode}
                />
            </div>

            <div className="h-px w-full bg-gray-500 my-4" />

            {/* By Creator */}
            <div className="w-full px-4 flex justify-between">
                <small className="xs text-white-400">Filter by Creator</small>
                {!!localFilter.length && (
                    <small
                        className="xs text-white hover:text-blue-500 cursor-pointer"
                        onClick={() => setLocalFilter([])}
                    >
                        Reset
                    </small>
                )}
            </div>
            <ScrollableArea className="mt-2 mb-4 flex-1 min-h-0">
                <div className="flex flex-col gap-[2px]">
                    {validWorkspaceMembers.map((member) => {
                        const onClick = () => {
                            if (localFilter.includes(member.userId)) {
                                setLocalFilter((prev) => prev.filter((f) => f !== member.userId))
                            } else {
                                setLocalFilter((prev) => prev.concat(member.userId))
                            }
                        }
                        return (
                            <div
                                key={member.userId}
                                className="flex items-center gap-3 px-4 py-[6px] hover:bg-gray-800 cursor-pointer transition-colors duration-20"
                                onClick={onClick}
                            >
                                <Checkbox
                                    isChecked={localFilter.includes(member.userId)}
                                    onClick={onClick}
                                    size="small"
                                />
                                <div className="w-5 min-w-[20px] aspect-square rounded-[5px] overflow-hidden">
                                    <ProfilePhoto
                                        userId={member.userId}
                                        fullName={member.fullName}
                                    />
                                </div>
                                <small className="w-full">{member.fullName || ""}</small>
                            </div>
                        )
                    })}
                </div>
            </ScrollableArea>

            {/* Buttons */}
            <div className="flex gap-2 px-3 py-2 border-t border-t-gray-500">
                <ButtonTertiary title="Cancel" className="w-full" onClick={closeMenu} />
                <ButtonPrimary
                    title="Apply"
                    className="w-full"
                    onClick={() => {
                        dispatch(setFilterAssetsValue(localFilter))
                        dispatch(setSortAssetsValue(localSortMode))
                        closeMenu()
                    }}
                />
            </div>
        </>
    )
}

function FilterPdfMenuContent({ closeMenu }: { closeMenu: () => void }) {
    const dispatch = useAppDispatch()
    const sortMode = useAppSelector(selectSortPDFsValue)
    const [localSortMode, setLocalSortMode] = useState(sortMode)
    const filterPdfValue = useAppSelector(selectFilterPDFsValue)
    const [localFilter, setLocalFilter] = useState(filterPdfValue)
    const workspaceMembers = useAppSelector(selectWorkspaceMembers)
    const pdfIdsByCreatorId = useAppSelector(selectPdfIdsByCreatorId)
    const validWorkspaceMembers = useMemo(() => {
        return workspaceMembers.filter((member) => pdfIdsByCreatorId[member.userId]?.length)
    }, [pdfIdsByCreatorId, workspaceMembers])
    const validWorkspaceMemberIds = useMemo(() => {
        return validWorkspaceMembers.map((member) => member.userId)
    }, [validWorkspaceMembers])
    useEffect(() => {
        // This is needed to handle deletes. If a user is part of the filter, then you delete all the
        // assets they created, they will be stuck in the filter. This removes all users from the
        // filter if they do not have any created assets.
        const invalidSelectedMemberIds = localFilter.filter(
            (id) => !validWorkspaceMemberIds.includes(id)
        )
        if (invalidSelectedMemberIds.length) {
            const validSelectedMemberIds = localFilter.filter((id) =>
                validWorkspaceMemberIds.includes(id)
            )
            dispatch(setFilterAssetsValue(validSelectedMemberIds))
            setLocalFilter(validSelectedMemberIds)
        }
    }, [dispatch, localFilter, validWorkspaceMemberIds, workspaceMembers])
    return (
        <>
            {/* Sort mode */}
            <div className="flex gap-3 items-center px-4">
                <h5 className="text-gray-100 whitespace-nowrap">Sort By</h5>
                <SortDropdown
                    sortOptions={pdfsSortOptions}
                    sortMode={localSortMode}
                    setSortMode={setLocalSortMode}
                />
            </div>

            <div className="h-px w-full bg-gray-500 my-4" />

            {/* By Creator */}
            <div className="w-full px-4 flex justify-between">
                <small className="xs text-white-400">Filter by Creator</small>
                {!!localFilter.length && (
                    <small
                        className="xs text-white hover:text-blue-500 cursor-pointer"
                        onClick={() => setLocalFilter([])}
                    >
                        Reset
                    </small>
                )}
            </div>
            <ScrollableArea className="mt-2 mb-4 flex-1 min-h-0">
                <div className="flex flex-col gap-[2px]">
                    {validWorkspaceMembers.map((member) => {
                        const onClick = () => {
                            if (localFilter.includes(member.userId)) {
                                setLocalFilter((prev) => prev.filter((f) => f !== member.userId))
                            } else {
                                setLocalFilter((prev) => prev.concat(member.userId))
                            }
                        }
                        return (
                            <div
                                key={member.userId}
                                className="flex items-center gap-3 px-4 py-[6px] hover:bg-gray-800 cursor-pointer transition-colors duration-20"
                                onClick={onClick}
                            >
                                <Checkbox
                                    isChecked={localFilter.includes(member.userId)}
                                    onClick={onClick}
                                    size="small"
                                />
                                <div className="w-5 min-w-[20px] aspect-square rounded-[5px] overflow-hidden">
                                    <ProfilePhoto
                                        userId={member.userId}
                                        fullName={member.fullName}
                                    />
                                </div>
                                <small className="w-full">{member.fullName || ""}</small>
                            </div>
                        )
                    })}
                </div>
            </ScrollableArea>

            {/* Buttons */}
            <div className="flex gap-2 px-3 py-2 border-t border-t-gray-500">
                <ButtonTertiary title="Cancel" className="w-full" onClick={closeMenu} />
                <ButtonPrimary
                    title="Apply"
                    className="w-full"
                    onClick={() => {
                        dispatch(setFilterPDFsValue(localFilter))
                        dispatch(setSortPDFsValue(localSortMode))
                        closeMenu()
                    }}
                />
            </div>
        </>
    )
}

function SortDropdown<T extends string>({
    sortOptions,
    sortMode,
    setSortMode,
}: {
    sortOptions: readonly T[]
    sortMode: T
    setSortMode: (s: T) => void
}) {
    const buttonRef = useRef<HTMLButtonElement | null>(null)
    const dispatch = useDispatch()
    const activeSidePanelSortContextMenu = useAppSelector(selectActiveContextMenu("SidePanelSort"))

    const contextMenuSections: ContextMenuItem[][] = useMemo(() => {
        return [
            sortOptions
                .filter((option) => option === "None")
                .map((option) => ({
                    title: option,
                    onClick: (e: React.MouseEvent) => {
                        e.stopPropagation()
                        setSortMode(option)
                    },
                    Icon: sortMode === option ? Icons.Checkmark : null,
                })),
            sortOptions
                .filter((option) => option !== "None")
                .map((option) => ({
                    title: option,
                    onClick: (e: React.MouseEvent) => {
                        e.stopPropagation()
                        setSortMode(option)
                    },
                    Icon: sortMode === option ? Icons.Checkmark : null,
                })),
        ].filter((section) => !!section.length)
    }, [setSortMode, sortMode, sortOptions])

    useEffect(() => {
        const menu = activeSidePanelSortContextMenu
        return () => {
            if (menu) {
                dispatch(setActiveContextMenu(undefined))
            }
        }
    })

    return (
        <>
            <button
                onClick={(e) => {
                    if (activeSidePanelSortContextMenu) {
                        return
                    }
                    e.stopPropagation()
                    const boundingClientRect = buttonRef.current?.getBoundingClientRect()
                    dispatch(
                        setActiveContextMenu({
                            type: "SidePanelSort",
                            position: {
                                top: (boundingClientRect?.y || 0) + 32,
                                left:
                                    (boundingClientRect?.x || 0) + (boundingClientRect?.width || 0),
                            },
                        })
                    )
                }}
                ref={buttonRef}
                className="flex justify-between items-center w-full bg-gray-900 pl-4 pr-2 py-1 text-white-1000 rounded-lg"
            >
                <h5 className="truncate">{sortMode}</h5>
                <Icons.ArrowDropDownIcon
                    dropdownOpen={!!activeSidePanelSortContextMenu}
                    setDropdownOpen={() => null}
                />
            </button>
            {activeSidePanelSortContextMenu && (
                <ContextMenu
                    sections={contextMenuSections}
                    onBlur={() => dispatch(setActiveContextMenu(undefined))}
                    isFixedPosition={true}
                    top={activeSidePanelSortContextMenu.position.top}
                    left={activeSidePanelSortContextMenu.position.left}
                    style={{
                        transform: "translateX(-100%)",
                    }}
                    keepOpenOnSelect
                    iconColor="#16C949" // green-500
                    containerClassName="border border-white-200"
                />
            )}
        </>
    )
}

export function SidePanelTabSelectAndSearchAndFilter() {
    const dispatch = useAppDispatch()
    const sidePanelMode = useAppSelector(selectSidePanelMode)
    const activeSidePanelContextMenu = useAppSelector(selectActiveContextMenu("SidePanelItem"))
    const isSearchAndFilterCollapsed = useAppSelector(selectIsSearchAndFilterCollapsed)
    const searchValue = useAppSelector(searchAndFilterDataByMode[sidePanelMode].selectSearchValue)
    const { hasFilter } = useHasFilter()
    const hasSort = useHasSort()
    return (
        <div className="px-3">
            <div className="w-full h-8 flex bg-white-100 rounded-lg overflow-hidden items-center">
                <div className="grow py-0.5">
                    <SegmentedControl
                        segments={sidePanelModes}
                        defaultSelectedIndex={sidePanelModes.findIndex((s) => s === sidePanelMode)}
                        onChange={(index) => {
                            dispatch(setSidePanelMode({ mode: sidePanelModes[index] }))
                            if (activeSidePanelContextMenu) {
                                dispatch(setActiveContextMenu(undefined))
                            }
                        }}
                        zIndex={zIndex.SidePanel}
                        className="!bg-transparent"
                        thumbClassName="!bg-white-200"
                    />
                </div>
                <div className="w-px h-full bg-white-100"></div>
                <div className="w-8 h-full shrink-0 p-0.5">
                    <button
                        className={classNames(
                            "hover:bg-white-200 w-full h-full flex justify-center items-center cursor-pointer rounded-md",
                            {
                                "text-gray-300 hover:text-gray-0":
                                    !searchValue && !hasFilter && !hasSort,
                                "text-blue-500": !!searchValue || hasFilter || hasSort,
                            }
                        )}
                        onClick={() => {
                            if (isSearchAndFilterCollapsed) {
                                dispatch(expandSearchAndFilter())
                            } else {
                                dispatch(collapseSearchAndFilter())
                            }
                        }}
                    >
                        <Icons.SmallIcon
                            Icon={isSearchAndFilterCollapsed ? Icons.MagnifyingGlass : Icons.XClose}
                        />
                    </button>
                </div>
            </div>
            <ExpandableContent isOpen={!isSearchAndFilterCollapsed}>
                <div className="pt-3">
                    <div className="py-3 px-px border-t border-b border-white-200">
                        <SidePanelSearchAndFilter />
                    </div>
                </div>
            </ExpandableContent>
        </div>
    )
}
